import { queryOptions } from '@tanstack/react-query'

import { fetchJson, isApiError, path } from '@/api/client'

/**
 * `/api/actions*`, `/api/rules*`, `/api/capabilities`, `/api/cliplib*` — the dynamic action registry
 * (docs/SKEL-STORY.md Part 3: §8 R6/R7, §9, and §11 "Open interactions", which wins over §9).
 *
 * An action is DATA: any number of named participants (roles) of any entity type — a character,
 * an object, a piece of scenery, a place on the stage (`feature`) or a named spot — with clauses,
 * a step program with one track per role, and results. A world rule is the same idea triggered by
 * an event (something lands in water, walks through a door…).
 *
 * The server is the source of truth for validation. These types are deliberately OPEN (extra keys
 * survive a round trip through the JSON editor) and every reader copes with missing fields, so a
 * newer backend never breaks this page.
 */

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                  */
/* -------------------------------------------------------------------------- */

export type Origin = 'builtin' | 'ai' | 'user'
export const ORIGINS = ['builtin', 'ai', 'user'] as const satisfies readonly Origin[]

/**
 * Where a registry document lives (the envelope's `source`): `seed` = the built-in file,
 * `override` = the parent's changed copy of a built-in (deleting it brings the original back),
 * `data` = one of the parent's (or the AI's) own.
 */
export type RegistrySource = 'seed' | 'override' | 'data'

/** §11.1 entity types. `feature` is a place on the stage (water, sky, a door…). */
export const ENTITY_TYPES = ['character', 'object', 'scenery', 'feature', 'spot'] as const
export type EntityType = (typeof ENTITY_TYPES)[number]

/** §9.1 categories (open: an AI action may bring its own). */
export const ACTION_CATEGORIES = ['play', 'social', 'locomotion', 'reaction', 'rest', 'twist', 'motion', 'effect'] as const
export type ActionCategory = (typeof ACTION_CATEGORIES)[number]

/** §11.6 rule trigger events. */
export const RULE_EVENTS = ['land', 'enter', 'leave', 'contact', 'drop', 'arrive', 'state_change'] as const
export type RuleEvent = (typeof RULE_EVENTS)[number]

/** §11.3 relations. */
export const RELATIONS = ['near', 'far', 'holding', 'held_by', 'on', 'inside', 'behind', 'taller_than', 'shorter_than', 'can_reach', 'same_region', 'in_region'] as const
export type Relation = (typeof RELATIONS)[number]

/* -------------------------------------------------------------------------- */
/* Contracts (§11.2–§11.6, with §9's older fields still readable)              */
/* -------------------------------------------------------------------------- */

/** §11.2 — one participant. Every field is optional; unknown keys are kept. */
export interface RoleSpec {
  types?: string[]
  required?: boolean
  kinds_any?: string[]
  plans_any?: string[]
  capabilities_all?: string[]
  capabilities_any?: string[]
  /** Sockets the participant must have (`hand`, `mouth`, …). */
  needs?: string[]
  /** Required current state, e.g. `{ "holder": "$thrower" }`. */
  state?: Record<string, unknown> | null
  /** §9.1 (older seeds): who holds the object. */
  held_by?: string
  [k: string]: unknown
}

/** §11.3 — a clause, discriminated by `k`. Kept loose on purpose (see the module note). */
export interface Clause {
  k: string
  [k2: string]: unknown
}

export interface MoveSpec {
  to?: string
  clip?: string | null
  stop?: string | null
  [k: string]: unknown
}

export interface TossSpec {
  from?: string
  to?: string
  apex?: number
  then?: string
  at_event?: string
  [k: string]: unknown
}

/** §11.4 — what one participant does during a step. */
export interface StepTrack {
  clip?: string | null
  face?: string | null
  layer?: string | null
  mask?: string | null
  loops?: number | null
  move?: MoveSpec | null
  attach?: { to?: string; socket?: string; [k: string]: unknown } | null
  detach?: { at?: string; fall?: boolean; [k: string]: unknown } | null
  toss?: TossSpec | null
  mount?: { on?: string; [k: string]: unknown } | null
  dismount?: unknown
  vanish?: boolean | null
  appear?: unknown
  react?: { fx?: string | null; clip?: string | null; [k: string]: unknown } | null
  sync?: { event?: string; with?: string; [k: string]: unknown } | null
  [k: string]: unknown
}

export interface FxSpec {
  kind: string
  at?: string
  at_s?: number | null
  on?: string
  [k: string]: unknown
}

/** §11.4 — one step of a program. §9.3's per-role keys (`actor: {...}`) are still read. */
export interface ProgramStep {
  phase?: string
  dur?: number | string
  tracks?: Record<string, StepTrack>
  fx?: FxSpec[]
  wait?: number
  [k: string]: unknown
}

/** §11.5 */
export interface ResultEffect {
  set: string
  to: unknown
  [k: string]: unknown
}

export interface FallbackSpec {
  action: string
  map?: Record<string, string>
  [k: string]: unknown
}

/** §9.1 as amended by §11.2. */
export interface ActionDef {
  id: string
  rev: number
  origin: Origin
  enabled: boolean
  label: string
  description: string
  category?: string
  idea_template?: string
  roles: Record<string, RoleSpec>
  preconditions?: Clause[]
  /** role -> clip name -> fallback ladder. */
  clips?: Record<string, Record<string, string[]>>
  program?: ProgramStep[]
  result?: ResultEffect[]
  fallback?: FallbackSpec[]
  duration?: { min?: number; default?: number; max?: number }
  fx_auto?: FxSpec[]
  /** From the envelope (never sent back): where it lives, and whether a seed backs it. */
  source?: RegistrySource
  builtin?: boolean
  [k: string]: unknown
}

export interface RuleWhen {
  event: string
  subject?: RoleSpec | null
  other?: RoleSpec | null
  state?: Record<string, unknown> | null
  [k: string]: unknown
}

/** §11.6 */
export interface InteractionRule {
  id: string
  rev: number
  origin: Origin
  enabled: boolean
  label?: string
  description: string
  when: RuleWhen
  then: ProgramStep[]
  result?: ResultEffect[]
  priority?: number
  once_per_pair?: boolean
  source?: RegistrySource
  builtin?: boolean
  [k: string]: unknown
}

/** §9.4 */
export interface ClipTemplate {
  name: string
  plan: string
  view_kind: string
  fps?: number
  frames?: number
  loop?: boolean
  tracks?: Array<{ bone: string; element: string; keys: Array<{ frame: number; value: number; ease?: string }> }>
  events?: Record<string, number>
  origin?: Origin
  description?: string
  rev?: number
  [k: string]: unknown
}

/** A capability map (§11.1): category / keyword / region / anchor → capability sets. Open. */
export type CapabilityMap = Record<string, unknown>

/**
 * `GET /api/capabilities`: `capabilities` = the built-in map with the parent's overlay on top;
 * `overlay` = just the parent's changes (per section key, a list that replaces the built-in one),
 * `rev` = what a `PUT` must name as `base_rev`.
 */
export interface CapabilitiesDoc {
  rev: number
  capabilities: CapabilityMap
  overlay: CapabilityMap | null
}

export type RegistryKind = 'action' | 'rule'

/* -------------------------------------------------------------------------- */
/* Lists                                                                       */
/* -------------------------------------------------------------------------- */

export interface ActionFilters {
  origin?: Origin
  category?: string
  q?: string
}

export interface RuleFilters {
  origin?: Origin
  event?: string
  q?: string
}

/** A list may come back as `{items}` or a bare array; either way, an array of objects. */
function itemsOf<T>(raw: unknown): T[] {
  const arr = Array.isArray(raw) ? raw : raw && typeof raw === 'object' && Array.isArray((raw as { items?: unknown }).items) ? (raw as { items: unknown[] }).items : []
  return arr.filter((x): x is T => !!x && typeof x === 'object')
}

/** Fill the fields every screen reads, so a thin summary row still renders. */
export function normalizeAction(raw: Partial<ActionDef> & { id: string }): ActionDef {
  return {
    ...raw,
    id: raw.id,
    rev: typeof raw.rev === 'number' ? raw.rev : 0,
    origin: (ORIGINS as readonly string[]).includes(raw.origin as string) ? (raw.origin as Origin) : 'user',
    enabled: raw.enabled !== false,
    label: raw.label || prettyId(raw.id),
    description: raw.description ?? '',
    roles: raw.roles && typeof raw.roles === 'object' ? raw.roles : {},
  }
}

export function normalizeRule(raw: Partial<InteractionRule> & { id: string }): InteractionRule {
  return {
    ...raw,
    id: raw.id,
    rev: typeof raw.rev === 'number' ? raw.rev : 0,
    origin: (ORIGINS as readonly string[]).includes(raw.origin as string) ? (raw.origin as Origin) : 'user',
    enabled: raw.enabled !== false,
    description: raw.description ?? '',
    when: raw.when && typeof raw.when === 'object' ? raw.when : { event: 'state_change' },
    then: Array.isArray(raw.then) ? raw.then : [],
  }
}

/** `jump_to` → "Jump to". */
export function prettyId(id: string): string {
  const t = id.replace(/[_-]+/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : id
}

/** A PUT/POST/GET may answer with the document or an envelope around it (`{body}`/`{action}`/`{rule}`). */
function unwrap<T extends { id: string }>(raw: unknown, key: 'action' | 'rule'): T {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>
    for (const k of [key, 'body', 'item']) {
      const inner = r[k]
      if (inner && typeof inner === 'object' && typeof (inner as { id?: unknown }).id === 'string') {
        // An envelope may carry rev/origin beside the body.
        const env: Record<string, unknown> = { ...(inner as Record<string, unknown>) }
        if (typeof r.rev === 'number' && typeof env.rev !== 'number') env.rev = r.rev
        if (typeof r.origin === 'string' && typeof env.origin !== 'string') env.origin = r.origin
        // Where it lives decides Delete vs "Reset to original" (never sent back: SERVER_FIELDS).
        if (r.source === 'seed' || r.source === 'override' || r.source === 'data') env.source = r.source
        if (typeof r.builtin === 'boolean') env.builtin = r.builtin
        return env as T
      }
    }
  }
  return raw as T
}

/* -------------------------------------------------------------------------- */
/* Actions                                                                     */
/* -------------------------------------------------------------------------- */

export async function listActions(filters: ActionFilters = {}, signal?: AbortSignal): Promise<ActionDef[]> {
  const raw = await fetchJson<unknown>('/api/actions', { query: { origin: filters.origin, category: filters.category, q: filters.q?.trim() || undefined }, signal })
  // Each row is the same envelope a GET answers (`{id, source, builtin, action}`).
  return itemsOf<Record<string, unknown>>(raw)
    .map((row) => unwrap<ActionDef>(row, 'action'))
    .filter((a) => typeof a.id === 'string')
    .map(normalizeAction)
}

/** The document exactly as stored (no filled-in defaults) — what a write sends back. */
async function rawAction(id: string, signal?: AbortSignal): Promise<ActionDef> {
  return unwrap<ActionDef>(await fetchJson<unknown>(path`/api/actions/${id}`, { signal }), 'action')
}

export async function getAction(id: string, signal?: AbortSignal): Promise<ActionDef> {
  return normalizeAction(await rawAction(id, signal))
}

/** `PUT /api/actions/{id}` `{base_rev, body}` — a builtin is copied to the data dir on its first edit. */
export async function putAction(id: string, baseRev: number, body: Record<string, unknown>): Promise<ActionDef> {
  return normalizeAction(unwrap<ActionDef>(await fetchJson<unknown>(path`/api/actions/${id}`, { method: 'PUT', body: { base_rev: baseRev, body } }), 'action'))
}

/** `POST /api/actions` — create from JSON. */
export async function createAction(body: Record<string, unknown>): Promise<ActionDef> {
  return normalizeAction(unwrap<ActionDef>(await fetchJson<unknown>('/api/actions', { method: 'POST', body }), 'action'))
}

/** `DELETE /api/actions/{id}` — data-dir only; a builtin answers 409. */
export function deleteAction(id: string): Promise<void> {
  return fetchJson<void>(path`/api/actions/${id}`, { method: 'DELETE' })
}

/** The enabled switch: always writes against the head rev (GET, then PUT). */
export async function setActionEnabled(id: string, enabled: boolean): Promise<ActionDef> {
  const head = await rawAction(id)
  return putAction(id, typeof head.rev === 'number' ? head.rev : 0, { ...stripServerFields(head), enabled })
}

/* -------------------------------------------------------------------------- */
/* Rules                                                                       */
/* -------------------------------------------------------------------------- */

export async function listRules(filters: RuleFilters = {}, signal?: AbortSignal): Promise<InteractionRule[]> {
  const raw = await fetchJson<unknown>('/api/rules', { query: { origin: filters.origin, event: filters.event, q: filters.q?.trim() || undefined }, signal })
  return itemsOf<Record<string, unknown>>(raw)
    .map((row) => unwrap<InteractionRule>(row, 'rule'))
    .filter((r) => typeof r.id === 'string')
    .map(normalizeRule)
}

async function rawRule(id: string, signal?: AbortSignal): Promise<InteractionRule> {
  return unwrap<InteractionRule>(await fetchJson<unknown>(path`/api/rules/${id}`, { signal }), 'rule')
}

export async function getRule(id: string, signal?: AbortSignal): Promise<InteractionRule> {
  return normalizeRule(await rawRule(id, signal))
}

export async function putRule(id: string, baseRev: number, body: Record<string, unknown>): Promise<InteractionRule> {
  return normalizeRule(unwrap<InteractionRule>(await fetchJson<unknown>(path`/api/rules/${id}`, { method: 'PUT', body: { base_rev: baseRev, body } }), 'rule'))
}

export async function createRule(body: Record<string, unknown>): Promise<InteractionRule> {
  return normalizeRule(unwrap<InteractionRule>(await fetchJson<unknown>('/api/rules', { method: 'POST', body }), 'rule'))
}

export function deleteRule(id: string): Promise<void> {
  return fetchJson<void>(path`/api/rules/${id}`, { method: 'DELETE' })
}

export async function setRuleEnabled(id: string, enabled: boolean): Promise<InteractionRule> {
  const head = await rawRule(id)
  return putRule(id, typeof head.rev === 'number' ? head.rev : 0, { ...stripServerFields(head), enabled })
}

/* -------------------------------------------------------------------------- */
/* Capabilities + clip library                                                 */
/* -------------------------------------------------------------------------- */

const isPlainObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/** `{rev, capabilities, overlay}`; an older server's bare map reads as a map with no overlay. */
export function readCapabilitiesDoc(raw: unknown): CapabilitiesDoc {
  if (isPlainObject(raw) && isPlainObject(raw.capabilities)) {
    return { rev: typeof raw.rev === 'number' ? raw.rev : 0, capabilities: raw.capabilities, overlay: isPlainObject(raw.overlay) ? raw.overlay : null }
  }
  const map = isPlainObject(raw) ? raw : {}
  return { rev: typeof map.rev === 'number' ? map.rev : 0, capabilities: map, overlay: null }
}

export async function getCapabilities(signal?: AbortSignal): Promise<CapabilitiesDoc> {
  return readCapabilitiesDoc(await fetchJson<unknown>('/api/capabilities', { signal }))
}

/** `PUT /api/capabilities {base_rev, body}` — `body` REPLACES the parent's whole overlay. */
export async function putCapabilities(baseRev: number, overlay: CapabilityMap): Promise<CapabilitiesDoc> {
  const body: CapabilityMap = { ...overlay }
  for (const k of ['rev', 'updated_at', 'version']) delete body[k]
  return readCapabilitiesDoc(await fetchJson<unknown>('/api/capabilities', { method: 'PUT', body: { base_rev: baseRev, body } }))
}

/** Every capability name mentioned anywhere in the map (for hints and filters). */
export function capabilityNames(map: CapabilityMap | null | undefined): string[] {
  const out = new Set<string>()
  const walk = (v: unknown, depth: number): void => {
    if (depth > 6 || v == null) return
    if (Array.isArray(v)) {
      for (const x of v) {
        if (typeof x === 'string') out.add(x.replace(/\?$/, ''))
        else walk(x, depth + 1)
      }
    } else if (typeof v === 'object') {
      for (const x of Object.values(v as Record<string, unknown>)) walk(x, depth + 1)
    }
  }
  walk(map, 0)
  return [...out].sort()
}

export interface ClipFilters {
  plan?: string
  view_kind?: string
}

export async function listClipTemplates(filters: ClipFilters = {}, signal?: AbortSignal): Promise<ClipTemplate[]> {
  const raw = await fetchJson<unknown>('/api/cliplib', { query: { plan: filters.plan, view_kind: filters.view_kind }, signal })
  return itemsOf<ClipTemplate>(raw)
}

export function getClipTemplate(plan: string, viewKind: string, name: string, signal?: AbortSignal): Promise<ClipTemplate> {
  return fetchJson<ClipTemplate>(path`/api/cliplib/${plan}/${viewKind}/${encodeURIComponent(name)}`, { signal })
}

export function deleteClipTemplate(plan: string, viewKind: string, name: string): Promise<void> {
  return fetchJson<void>(path`/api/cliplib/${plan}/${viewKind}/${encodeURIComponent(name)}`, { method: 'DELETE' })
}

/* -------------------------------------------------------------------------- */
/* AI author + preview                                                         */
/* -------------------------------------------------------------------------- */

export interface AuthorRequest {
  kind: RegistryKind
  description: string
  name?: string
}

/** `POST /api/actions/author` → 202 `{job_id}` (R4, §11.7). */
export function startAuthor(req: AuthorRequest): Promise<{ job_id: number }> {
  const body: AuthorRequest = { kind: req.kind, description: req.description.trim() }
  const name = req.name?.trim()
  if (name) body.name = name
  return fetchJson<{ job_id: number }>('/api/actions/author', { method: 'POST', body })
}

/** What an author job's `result` tells us, whichever way the job wrote it. */
export interface AuthoredRef {
  kind: RegistryKind
  /** The saved id, when the job saved it (R4 saves with `origin:'ai'`). */
  id: string | null
  /** The document itself, when the job returned it. */
  doc: Record<string, unknown> | null
  /** False only when the job says it did NOT save (then Keep creates it). */
  saved: boolean
}

export function readAuthorResult(kind: RegistryKind, result: Record<string, unknown> | null | undefined): AuthoredRef {
  const r = result ?? {}
  const docKey = kind === 'rule' ? 'rule' : 'action'
  const docRaw = r[docKey] ?? r.body ?? r.doc
  const doc = docRaw && typeof docRaw === 'object' && !Array.isArray(docRaw) ? (docRaw as Record<string, unknown>) : null
  const idCandidates = [r[`${docKey}_id`], r.id, doc?.id]
  const id = (idCandidates.find((x) => typeof x === 'string' && x.length > 0) as string | undefined) ?? null
  const k = r.kind === 'rule' || r.kind === 'action' ? (r.kind as RegistryKind) : kind
  return { kind: k, id, doc, saved: r.saved !== false }
}

export interface PreviewRequest {
  /** Role name → puppet uid: who plays which part. Roles left out are chosen by the server. */
  roles?: Record<string, string>
  /** Puppet uids for the remaining character roles, in role order. */
  cast?: string[]
  /** A puppet/catalog uid for the first remaining object role. */
  object?: string
}

/** A preview bundle, plus where its relative asset paths resolve from. */
export interface PreviewBundle {
  bundle: unknown
  /** Absolute or root-relative url whose directory is the bundle's base (§4.6.4 of the player). */
  bundleUrl: string
}

/** Where a preview's relative asset paths resolve when the server does not say. */
export function defaultPreviewUrl(id: string): string {
  return path`/api/actions/${id}/preview/bundle.json`
}

/**
 * `POST /api/actions/{id}/preview` → a mini story bundle that plays just this action (R6).
 * Accepts the bundle itself, or `{bundle, bundle_url?}`.
 */
export async function previewAction(id: string, req: PreviewRequest = {}, signal?: AbortSignal): Promise<PreviewBundle> {
  const body: PreviewRequest = {}
  const roles = Object.fromEntries(Object.entries(req.roles ?? {}).filter(([, uid]) => uid))
  if (Object.keys(roles).length > 0) body.roles = roles
  if (req.cast && req.cast.length > 0) body.cast = req.cast
  if (req.object) body.object = req.object
  const raw = await fetchJson<unknown>(path`/api/actions/${id}/preview`, { method: 'POST', body, signal })
  return readPreview(id, raw)
}

/** `POST /api/rules/{id}/preview` → a mini story in which the rule's event happens (same answer). */
export async function previewRule(id: string, req: PreviewRequest = {}, signal?: AbortSignal): Promise<PreviewBundle> {
  const roles = Object.fromEntries(Object.entries(req.roles ?? {}).filter(([, uid]) => uid))
  const body: PreviewRequest = Object.keys(roles).length > 0 ? { roles } : {}
  const raw = await fetchJson<unknown>(path`/api/rules/${id}/preview`, { method: 'POST', body, signal })
  return readPreview(id, raw)
}

export function readPreview(id: string, raw: unknown): PreviewBundle {
  if (raw && typeof raw === 'object') {
    const r = raw as Record<string, unknown>
    if (r.bundle && typeof r.bundle === 'object') {
      const url = typeof r.bundle_url === 'string' ? r.bundle_url : typeof r.base_url === 'string' ? r.base_url : defaultPreviewUrl(id)
      return { bundle: r.bundle, bundleUrl: url }
    }
  }
  return { bundle: raw, bundleUrl: defaultPreviewUrl(id) }
}

/* -------------------------------------------------------------------------- */
/* Editor contract: vocabulary + live validation                               */
/* -------------------------------------------------------------------------- */

/** One per-role step-track field the interpreter understands (`GET /api/actions/meta`). */
export interface TrackFieldInfo {
  name: string
  type: 'clip' | 'ref' | 'bool' | 'number' | 'object'
  help: string
  /** Additive (the server may send them): plain-word label, "keep behind More", pick-one values. */
  label?: string
  advanced?: boolean
  choices?: string[]
}

/**
 * `GET /api/actions/meta` — every word the manual editor offers, derived on the server from the
 * real models / interpreter constants (so a new stop, socket or fx shows up without a frontend
 * change).
 */
export interface ActionsMeta {
  entity_types: string[]
  categories: string[]
  phases: string[]
  clause_kinds: string[]
  relations: string[]
  /** Rule trigger events. */
  events: string[]
  /** `move.stop` values. */
  stops: string[]
  toss_then: string[]
  sockets: string[]
  fx_kinds: string[]
  layers: string[]
  masks: string[]
  capabilities: string[]
  clip_names: string[]
  plans: string[]
  /** Puppet kinds (person, animal…). */
  kinds: string[]
  state_keys: string[]
  track_fields: TrackFieldInfo[]
  ref_help: string
  /** Additive: moments a `role:moment` ref may name (`release`, `arrive`…). */
  moments: string[]
  /** Additive: ids the API keeps for itself (`meta`, `validate`, `author`). */
  reserved_ids: string[]
  /** Additive: capability → one line of meaning. */
  capability_help?: Record<string, string>
}

/** The server's answer as sent; the editor merges it over its built-in defaults. */
export function getActionsMeta(signal?: AbortSignal): Promise<Partial<ActionsMeta>> {
  return fetchJson<Partial<ActionsMeta>>('/api/actions/meta', { signal })
}

/** A problem at a dotted path of the document (`roles.thrower.types`, `program.2.tracks.ball.toss.to`). */
export interface ValidationIssue {
  path: string
  message: string
}

export interface ValidationResult {
  ok: boolean
  errors: ValidationIssue[]
  warnings: ValidationIssue[]
  normalized: Record<string, unknown> | null
}

/** Top-level keys a message may start with (`program[0].tracks.x: …`); anything else is plain text. */
const DOC_KEYS = new Set([
  'id', 'label', 'description', 'category', 'idea_template', 'enabled', 'roles', 'preconditions', 'clips', 'program', 'result',
  'fallback', 'duration', 'fx_auto', 'when', 'then', 'priority', 'once_per_pair', 'supersedes',
]) // prettier-ignore

/** `body.program[2].tracks` → `program.2.tracks`. */
export function dotPath(raw: unknown): string {
  const parts = Array.isArray(raw) ? raw.map(String) : [String(raw ?? '')]
  return parts
    .join('.')
    .replace(/\[(\d+)\]/g, '.$1')
    .split('.')
    .filter((p) => p !== '')
    .filter((p, i) => !(i === 0 && p === 'body'))
    .join('.')
}

const LEAD_RE = /^([a-z_][a-z0-9_]*(?:\.[A-Za-z0-9_]+|\[\d+\])*)\s*:\s+([\s\S]+)$/

/**
 * "program[0].tracks.bal: unknown role; clips: …" (a model validator's joined lines, maybe behind
 * `body: `) → one issue per `path: message` part; paths in the text are from the document root.
 * Text without a leading path stays at `base`, in one piece.
 */
function splitMessage(base: string, message: string): ValidationIssue[] {
  const text = message.replace(/^Value error,\s*/, '').trim()
  const out: ValidationIssue[] = []
  for (const part of text.split(/;\s+/)) {
    const m = LEAD_RE.exec(part.trim())
    if (m && m[1] === 'body') {
      out.push(...splitMessage(base, m[2]!))
      continue
    }
    const head = m ? dotPath(m[1]) : ''
    if (m && head && DOC_KEYS.has(head.split('.')[0]!)) out.push({ path: head, message: m[2]!.replace(/^Value error,\s*/, '').trim() })
    else out.push({ path: base, message: part.trim() })
  }
  return out.length > 1 && out.every((x) => x.path === base) ? [{ path: base, message: text }] : out
}

/**
 * Rows of `errors` / `warnings` in any shape the server uses — `{path, message}`, pydantic
 * `{loc, msg}`, or `"path: message"` strings (joined with `; ` by a model validator) — as issues
 * with dotted paths.
 */
export function readIssues(rows: unknown): ValidationIssue[] {
  if (!Array.isArray(rows)) return []
  const out: ValidationIssue[] = []
  for (const row of rows) {
    if (typeof row === 'string') {
      out.push(...splitMessage('', row))
    } else if (row && typeof row === 'object') {
      const o = row as { path?: unknown; loc?: unknown; message?: unknown; msg?: unknown }
      const message = typeof o.message === 'string' ? o.message : typeof o.msg === 'string' ? o.msg : JSON.stringify(row)
      const base = dotPath(o.path ?? o.loc ?? '')
      out.push(...splitMessage(base, message))
    }
  }
  const seen = new Set<string>()
  return out.filter((i) => {
    const key = `${i.path}\u0000${i.message}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/**
 * `POST /api/actions/validate` or `/api/rules/validate` `{body}` → always 200 `{ok, errors, warnings, normalized}`.
 * `savedId`: the id a stored document is saved under (what its `PUT` names), so a draft whose id was
 * changed is flagged while editing rather than on save.
 */
export async function validateDoc(kind: RegistryKind, body: Record<string, unknown>, signal?: AbortSignal, savedId?: string): Promise<ValidationResult> {
  const url = kind === 'rule' ? '/api/rules/validate' : '/api/actions/validate'
  const payload: Record<string, unknown> = { body: stripServerFields(body) }
  if (savedId) payload.id = savedId
  const raw = await fetchJson<Record<string, unknown>>(url, { method: 'POST', body: payload, signal })
  const errors = readIssues(raw?.errors)
  return {
    ok: raw?.ok === true && errors.length === 0,
    errors,
    warnings: readIssues(raw?.warnings),
    normalized: raw && typeof raw.normalized === 'object' && raw.normalized && !Array.isArray(raw.normalized) ? (raw.normalized as Record<string, unknown>) : null,
  }
}

/** The issues of a failed save (a 422's `details.errors`); `[]` for any other failure. */
export function validationIssues(err: unknown): ValidationIssue[] {
  if (!isApiError(err) || err.status !== 422) return []
  const d = err.details
  // Prefer `{path, message}` rows (`issues` on a refused create, `errors` on a refused save).
  const rowsOf = (v: unknown): boolean => Array.isArray(v) && v.some((r) => !!r && typeof r === 'object')
  const rows = [d.issues, d.errors, d.detail].find(rowsOf) ?? [d.errors, d.issues, d.detail].find(Array.isArray)
  const issues = readIssues(rows)
  return issues.length > 0 ? issues : [{ path: '', message: err.message || 'The studio did not accept it.' }]
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

/** Fields the server owns (or the envelope carries); never sent back in a PUT/POST body. */
const SERVER_FIELDS = ['rev', 'origin', 'created_at', 'updated_at', 'updated_by', 'source', 'builtin'] as const

export function stripServerFields<T extends Record<string, unknown>>(doc: T): Record<string, unknown> {
  const out: Record<string, unknown> = { ...doc }
  for (const k of SERVER_FIELDS) delete out[k]
  return out
}

/**
 * The server's validation messages for a 422 (`details.errors`: strings or pydantic
 * `{loc, msg}` rows), else the error's own message. Never empty for an error.
 */
export function validationMessages(err: unknown): string[] {
  if (!err) return []
  if (isApiError(err)) {
    const d = err.details
    const rows = [d.errors, d.messages, d.issues, d.detail].find(Array.isArray) as unknown[] | undefined
    const out: string[] = []
    for (const row of rows ?? []) {
      if (typeof row === 'string') out.push(row)
      else if (row && typeof row === 'object') {
        const o = row as { loc?: unknown; msg?: unknown; message?: unknown; path?: unknown }
        const msg = typeof o.msg === 'string' ? o.msg : typeof o.message === 'string' ? o.message : JSON.stringify(row)
        const where = Array.isArray(o.loc) ? o.loc : Array.isArray(o.path) ? o.path : typeof o.path === 'string' ? [o.path] : []
        const loc = where.filter((p) => p !== 'body').join('.')
        out.push(loc ? `${loc}: ${msg}` : msg)
      }
    }
    if (out.length > 0) {
      const msg = err.message
      if (!msg || msg === 'Request does not match the API contract') return out
      // The sentence first; a line it already says ("…: nothing here can be the actor") is not repeated.
      return [msg, ...out.filter((line) => line !== msg && !msg.includes(line))]
    }
    return [err.message || `HTTP ${err.status}`]
  }
  return [err instanceof Error && err.message ? err.message : String(err)]
}

/** A fresh id from `base` that is not in `taken`: `throw_copy`, `throw_copy_2`, … */
export function freeId(base: string, taken: Iterable<string>): string {
  const used = new Set(taken)
  const stem = `${base.replace(/_copy(_\d+)?$/, '')}_copy`.slice(0, 48)
  if (!used.has(stem)) return stem
  for (let n = 2; n < 1000; n++) if (!used.has(`${stem}_${n}`)) return `${stem}_${n}`
  return `${stem}_${Date.now().toString(36)}`
}

/* -------------------------------------------------------------------------- */
/* Keys + query options                                                        */
/* -------------------------------------------------------------------------- */

function clean(f: object): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(f)) if (typeof v === 'string' && v.trim() !== '') out[k] = v
  return out
}

/** Own key family (`['actions', …]`) so SSE / other tracks can invalidate by prefix. */
export const actionKeys = {
  all: () => ['actions'] as const,
  list: (f: ActionFilters = {}) => ['actions', 'list', clean(f)] as const,
  detail: (id: string) => ['actions', 'detail', id] as const,
  rules: () => ['actions', 'rules'] as const,
  ruleList: (f: RuleFilters = {}) => ['actions', 'rules', 'list', clean(f)] as const,
  rule: (id: string) => ['actions', 'rules', 'detail', id] as const,
  capabilities: () => ['actions', 'capabilities'] as const,
  cliplib: (f: ClipFilters = {}) => ['actions', 'cliplib', clean(f)] as const,
  meta: () => ['actions', 'meta'] as const,
}

/** The editor's vocabulary. Not retried: without it the editor uses its built-in defaults. */
export const actionsMetaQuery = queryOptions({
  queryKey: actionKeys.meta(),
  queryFn: ({ signal }) => getActionsMeta(signal),
  staleTime: 5 * 60_000,
  retry: false,
})

export const actionsQuery = (f: ActionFilters = {}) =>
  queryOptions({
    queryKey: actionKeys.list(f),
    queryFn: ({ signal }) => listActions(f, signal),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

export const actionQuery = (id: string) =>
  queryOptions({
    queryKey: actionKeys.detail(id),
    queryFn: ({ signal }) => getAction(id, signal),
    staleTime: 5_000,
  })

export const rulesQuery = (f: RuleFilters = {}) =>
  queryOptions({
    queryKey: actionKeys.ruleList(f),
    queryFn: ({ signal }) => listRules(f, signal),
    staleTime: 10_000,
    placeholderData: (prev) => prev,
  })

export const ruleQuery = (id: string) =>
  queryOptions({
    queryKey: actionKeys.rule(id),
    queryFn: ({ signal }) => getRule(id, signal),
    staleTime: 5_000,
  })

export const capabilitiesQuery = queryOptions({
  queryKey: actionKeys.capabilities(),
  queryFn: ({ signal }) => getCapabilities(signal),
  staleTime: 60_000,
  retry: false,
})

export const clipLibraryQuery = (f: ClipFilters = {}) =>
  queryOptions({
    queryKey: actionKeys.cliplib(f),
    queryFn: ({ signal }) => listClipTemplates(f, signal),
    staleTime: 30_000,
    retry: false,
  })
